import React, { useEffect, useState } from 'react';
import {
  Breadcrumb,
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Space,
  Switch,
  Select,
} from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import plPL from 'antd/lib/locale/pl_PL';
import 'moment/locale/pl';
import 'moment-timezone';
import FormCoverUpload from 'modules/galleryModules/components/FormCoverUpload';
import TextEditor from 'modules/layoutModule/components/TextEditor/TextEditor';
import { FieldData } from 'rc-field-form/lib/interface';
import {
  createGtwEdition,
  getGtwFiles,
  getGtwRequirements,
} from '../requests/gtwRequests';

const GtwEditionAdd = (): JSX.Element => {
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);
  const [form] = Form.useForm();
  const [fileImage1, setFileImage1] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldData[]>([]);
  const [files, setFiles] = useState([]);
  const [requirements, setRequirements] = useState([]);

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const onSubmitForm = (values: any) => {
    toggleButtonLoading(true);
    createGtwEdition(values).then(() => {
      toggleButtonLoading(false);
      form.resetFields();
      setFileImage1(null);
    });
  };

  useEffect(() => {
    getGtwFiles().then((response) => {
      if (response) {
        const activeFiles = response.filter(
          (file: { isActive: number }) => file.isActive
        );
        const mapFiles = activeFiles.map(
          (file: { title: string; id: string }) => {
            return {
              label: file.title,
              value: file.id,
            };
          }
        );
        mapFiles.unshift({ label: '', value: '' });
        setFiles(mapFiles);
      }
    });

    getGtwRequirements().then((response) => {
      if (response) {
        const activeRequirements = response.filter(
          (requirement: { isActive: number }) => requirement.isActive
        );
        const mapRequirements = activeRequirements.map(
          (requirement: { title: string; id: string }) => {
            return {
              label: requirement.title,
              value: requirement.id,
            };
          }
        );
        mapRequirements.unshift({ label: '', value: '' });
        setRequirements(mapRequirements);
      }
    });
  }, []);

  const onContentEdit = (e: string, field: string) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
  };

  const updateCover = (
    e: string,
    field: string,
    coverMethod: (value: string) => void
  ) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
    coverMethod(e);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Grunt to Wiedza</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/gtw-editions">Edycje</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/gtw-editions/add">+</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <div className="site-layout-background">
        <ConfigProvider locale={plPL}>
          <Form
            layout="vertical"
            validateMessages={validateMessages}
            onFinish={onSubmitForm}
            form={form}
            fields={fields}
          >
            <Form.Item
              name="fileImage1"
              label="Okładka"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <FormCoverUpload
                cover={fileImage1}
                setCover={(e) => updateCover(e, 'fileImage1', setFileImage1)}
                isNew
                field="fileImage1"
                number={1}
                hideTags
              />
            </Form.Item>

            <Form.Item name="status" label="Status">
              <Select
                showSearch
                placeholder="Wybierz status"
                optionFilterProp="children"
                options={[
                  { label: null, value: null },
                  { label: 'Wkrótce', value: 'gtw_main/status/wkrotce' },
                  { label: 'Aktualna', value: 'gtw_main/status/aktualny' },
                  { label: 'Zakończona', value: 'gtw_main/status/zakonczona' },
                ]}
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="startTime" label="Start rejestracji">
              <Input />
            </Form.Item>

            <Form.Item
              name="dateStart"
              label="Data rozpoczęcia"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              {/*// @ts-ignore*/}
              <DatePicker />
            </Form.Item>

            <Form.Item
              name="dateEnd"
              label="Data zakończenia"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              {/*// @ts-ignore*/}
              <DatePicker />
            </Form.Item>

            <Form.Item
              name="label"
              label="Nazwa w sliderze"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="name"
              label="Nazwa edycji"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="name2"
              label="Nazwa na podstronie produktu"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="edition"
              label="O edycji programu"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'edition')}
                disableGallery
              />
            </Form.Item>

            <Form.Item name="idRequirement1" label="Warunek 1">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement2" label="Warunek 2">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement3" label="Warunek 3">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement4" label="Warunek 4">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement5" label="Warunek 5">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement6" label="Warunek 6">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement7" label="Warunek 7">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement8" label="Warunek 8">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement9" label="Warunek 9">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idRequirement10" label="Warunek 10">
              <Select
                showSearch
                placeholder="Wybierz wymaganie"
                optionFilterProp="children"
                options={requirements}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="howToUse" label="Jak wziąć udział">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'howToUse')}
                disableGallery
              />
            </Form.Item>

            <Form.Item name="userList" label="Lista uczestników">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'userList')}
                disableGallery
              />
            </Form.Item>

            <Form.Item name="documents" label="Dokumenty">
              {/* @ts-ignore */}
              <TextEditor
                onChange={(e) => onContentEdit(e, 'documents')}
                disableGallery
              />
            </Form.Item>

            <Form.Item name="idFile1" label="Plik do pobrania 1">
              <Select
                showSearch
                placeholder="Wybierz plik"
                optionFilterProp="children"
                options={files}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idFile2" label="Plik do pobrania 2">
              <Select
                showSearch
                placeholder="Wybierz plik"
                optionFilterProp="children"
                options={files}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idFile3" label="Plik do pobrania 3">
              <Select
                showSearch
                placeholder="Wybierz plik"
                optionFilterProp="children"
                options={files}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item name="idFile4" label="Plik do pobrania 4">
              <Select
                showSearch
                placeholder="Wybierz plik"
                optionFilterProp="children"
                options={files}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>
            <Form.Item name="idFile5" label="Plik do pobrania 5">
              <Select
                showSearch
                placeholder="Wybierz plik"
                optionFilterProp="children"
                options={files}
                allowClear
                filterOption={(input, option) => {
                  // @ts-ignore
                  if (option?.label) {
                    // @ts-ignore
                    return option.label
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }
                  return false;
                }}
              />
            </Form.Item>

            <Form.Item
              name="isActive"
              label="Aktywność"
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSaveButtonLoading}
                >
                  Zapisz
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </ConfigProvider>
      </div>
    </>
  );
};

export default GtwEditionAdd;
